import React from 'react';
import {
  Callout, DateRangePicker, Toolbar, TrendChart, cn, formatThaiDate, resolveLink, secondaryBtnCls,
  type NavLinkComponent,
} from '../components';
import { Icon } from '../components/base/Icon';

/**
 * DashboardScreen — ภาพรวม (FR-RPT-01 · FR-RPT-06 · FR-RPT-09)
 *
 * แต่ละการ์ดมีสองชั้น:
 *   ณ ตอนนี้      — ไม่ขึ้นกับช่วงที่เลือก (คงค้าง · รอตรวจ · สมาชิกทั้งหมด)
 *   ในช่วงที่เลือก — ตัวเลขรวม + กราฟตามวัน/เดือน จาก `dashboard-summary/`
 *
 *   1. **ตัวเลือกช่วงอยู่แถวเดียวเหนือทุกการ์ด** — ทุกตัวเลขในช่วงใช้ช่วงเดียวกัน ไม่มีการ์ดไหนเลือกเอง
 *   2. **โหลดช่วงใหม่แล้วกรอบเดิมไม่หาย** — ตัวเลขเดิมจางลงระหว่างรอ ไม่กระพริบเป็นโครงว่าง
 *   3. **แต่ละส่วนล้มแยกกัน** — และถ้า host ยังไม่มีข้อมูลตามช่วง ตัวเลข "ณ ตอนนี้" ยังขึ้นได้
 *   4. **คะแนนคงค้างเด่นที่สุดในหมวดคะแนน** — เป็นภาระผูกพันทางบัญชี
 */

/* ---------------------------------------------------------------- ชนิดข้อมูล */

export type Section<T> = { state: 'loading' } | { state: 'error' } | { state: 'ready'; data: T };

export interface ClaimOverview {
  pending: number;
  overdue: number;
  overdueIsLowerBound: boolean;
  oldestPendingHours: number | null;
  /** คำขอรอตรวจของใบเสร็จที่ถูกยกเลิกภายหลัง · undefined = ไม่ทราบ */
  pendingCanceledReceipt?: number;
  slaHours: number;
}

export interface MemberOverview {
  total: number;
  byTier: Array<{ name: string; count: number }>;
  /** undefined = คำนวณจาก total − ผลรวมระดับ */
  withoutTier?: number;
}

export interface PointOverview {
  outstanding: number;
  accounts: number;
  expiring: number;
  expireDays: number;
}

export interface ChannelRow {
  label: string;
  submitted: number;
  approved: number;
  rejected: number;
  approvedAmount: number;
}

/** ตัวเลขในช่วงที่เลือก — ทุก series ยาวเท่ากับ labels */
export interface PeriodFlow {
  labels: string[];
  fullLabels: string[];
  members: { newInPeriod: number; unknownJoinDate: number; newSeries: number[] };
  points: {
    earned: number; redeemed: number; expired: number; adjustedAdd: number; adjustedDeduct: number;
    earnedSeries: number[]; redeemedSeries: number[];
  };
  claims: {
    submitted: number; approved: number; rejected: number; approvedAmount: number;
    avgReviewHours: number | null; channels: ChannelRow[]; submittedSeries: number[];
  };
  orders: {
    count: number; gross: number; discount: number; net: number; refunded: number;
    canceledCount: number; avgTicket: number; memberCount: number; memberNet: number; netSeries: number[];
  };
}

export type PeriodPreset = 'last7' | 'last30' | 'thisMonth' | 'last12m' | 'custom';

export interface DashboardPeriod {
  preset: PeriodPreset;
  /** YYYY-MM-DD (ค.ศ.) */
  from: string;
  to: string;
  interval: 'day' | 'month';
}

export interface DashboardScreenProps {
  claims: Section<ClaimOverview>;
  members: Section<MemberOverview>;
  points: Section<PointOverview>;
  period: DashboardPeriod;
  onPeriodChange?: (preset: PeriodPreset, custom?: { from: string; to: string }) => void;
  /** unavailable = ระบบยังไม่มีข้อมูลตามช่วงเวลา (host เก่า) */
  flow: Section<PeriodFlow> | { state: 'unavailable' };
  /** กำลังโหลดช่วงใหม่ทับข้อมูลเดิม — แสดงของเดิมแบบจางไว้ก่อน */
  flowRefreshing?: boolean;
  /** ข้อความ error จาก backend (เช่น ช่วงยาวเกิน) */
  flowError?: string;
  updatedAt?: string;
  refreshing?: boolean;
  onRefresh?: () => void;
  linkComponent?: NavLinkComponent;
}

/* ------------------------------------------------------------------ ตัวช่วย */

const num = (n: number, digits = 0) =>
  n.toLocaleString('th-TH', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const pts = (n: number) => n.toLocaleString('th-TH', { maximumFractionDigits: 2 });
const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ageText(hours: number): string {
  if (hours < 1) return 'ไม่ถึง 1 ชั่วโมง';
  if (hours < 48) return `${num(Math.floor(hours))} ชั่วโมง`;
  return `${num(Math.floor(hours / 24))} วัน`;
}

function updatedText(iso?: string): string {
  if (!iso) return 'กำลังโหลด…';
  const d = new Date(iso);
  const date = d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
  return `ข้อมูล ณ ${date} ${time} น.`;
}

const PRESETS: Array<{ id: Exclude<PeriodPreset, 'custom'>; label: string }> = [
  { id: 'last7', label: '7 วัน' },
  { id: 'last30', label: '30 วัน' },
  { id: 'thisMonth', label: 'เดือนนี้' },
  { id: 'last12m', label: '12 เดือน' },
];

type Tone = 'default' | 'brand' | 'warning';
const TONE_TEXT: Record<Tone, string> = {
  default: 'text-[var(--ink-900)]',
  brand: 'text-[var(--blue-700)]',
  warning: 'text-[var(--warning-text)]',
};

/* --------------------------------------------------------------- ชิ้นส่วนย่อย */

const Card: React.FC<{ title: string; icon: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title, icon, action, children,
}) => (
  <section
    aria-label={title}
    className="flex min-w-0 flex-col gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-4)]"
  >
    <header className="flex flex-wrap items-center gap-[var(--space-2)]">
      <h3 className="flex items-center gap-[var(--space-1)] text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">
        <Icon name={icon} size={20} />
        {title}
      </h3>
      {action && <div className="ml-auto">{action}</div>}
    </header>
    {/* การ์ดกว้างเต็มแถว — ชั้น "ณ ตอนนี้" กับ "ในช่วง" วางข้างกัน จอแคบลงจะตกเป็นบน–ล่างเอง */}
    <div className="flex flex-wrap items-start gap-x-[var(--space-6)] gap-y-[var(--space-4)]">{children}</div>
  </section>
);

/**
 * หัวข้อย่อยของชั้น — บอกว่าตัวเลขข้างล่างเป็น "ณ ตอนนี้" หรือ "ในช่วง"
 * now  = แคบ (ตัวเลขไม่กี่ช่อง) · flow = กว้างกว่าสองเท่า (ตัวเลข + กราฟ + ตาราง)
 */
const LAYER_FLEX: Record<'now' | 'flow', React.CSSProperties> = {
  now: { flex: '1 1 calc(var(--space-20) * 4)', minWidth: 0 },
  flow: { flex: '2.5 1 calc(var(--space-20) * 6)', minWidth: 0 },
};

const Layer: React.FC<{ title: string; children: React.ReactNode; dim?: boolean; size?: 'now' | 'flow' }> = ({
  title, children, dim, size = 'flow',
}) => (
  <div
    style={LAYER_FLEX[size]}
    className={cn('flex flex-col gap-[var(--space-2)] transition-opacity', dim && 'opacity-50')}
    aria-busy={dim || undefined}
  >
    <h4 className="text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] uppercase leading-[var(--leading-table)] text-[var(--ink-600)]">
      {title}
    </h4>
    {children}
  </div>
);

const Stat: React.FC<{
  label: string; value: string; unit?: string; note?: React.ReactNode; tone?: Tone; icon?: string;
  emphasis?: boolean; wide?: boolean;
}> = ({ label, value, unit, note, tone = 'default', icon, emphasis, wide }) => (
  <div
    style={wide ? { gridColumn: '1 / -1' } : undefined}
    className={cn(
      'flex min-w-0 flex-col gap-[var(--space-05)] rounded-[var(--radius-sm)] p-[var(--space-3)]',
      emphasis ? 'bg-[var(--blue-050)]' : 'bg-[var(--surface-100)]',
    )}
  >
    <span className="flex items-center gap-[var(--space-1)] text-[length:var(--text-form)] leading-[var(--leading-form)] text-[var(--ink-700)]">
      {icon && <Icon name={icon} size={16} />}
      {label}
    </span>
    <span className={cn('flex flex-wrap items-baseline gap-[var(--space-1)]', TONE_TEXT[tone])}>
      <strong
        className={cn(
          'font-[var(--weight-bold)] leading-[var(--leading-num)]',
          emphasis ? 'text-[length:var(--text-title)]' : 'text-[length:var(--text-title-sm)]',
        )}
      >
        {value}
      </strong>
      {unit && <span className="text-[length:var(--text-form)] text-[var(--ink-700)]">{unit}</span>}
    </span>
    {note && <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">{note}</span>}
  </div>
);

const StatGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid gap-[var(--space-2)]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(calc(var(--space-20) * 2), 1fr))' }}>
    {children}
  </div>
);

const Skeleton: React.FC<{ label: string }> = ({ label }) => (
  <div aria-busy="true" className="flex flex-col gap-[var(--space-2)]">
    <span className="sr-only">กำลังโหลด{label}</span>
    <div className="h-[var(--space-16)] animate-pulse rounded-[var(--radius-sm)] bg-[var(--surface-100)]" />
    <div className="h-[var(--space-16)] animate-pulse rounded-[var(--radius-sm)] bg-[var(--surface-100)]" />
  </div>
);

const ErrorLine: React.FC<{ label: string; detail?: string }> = ({ label, detail }) => (
  <p role="status" className="flex items-start gap-[var(--space-1)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--error-text)]">
    <Icon name="error" size={18} />
    <span>โหลด{label}ไม่สำเร็จ{detail ? ` — ${detail}` : ' — กด "โหลดใหม่" ด้านบนเพื่อลองอีกครั้ง'}</span>
  </p>
);

function NowBody<T>({ section, label, children }: { section: Section<T>; label: string; children: (d: T) => React.ReactNode }) {
  if (section.state === 'loading') return <Skeleton label={label} />;
  if (section.state === 'error') return <ErrorLine label={label} />;
  return <>{children(section.data)}</>;
}

/* -------------------------------------------------------------------- หน้าจอ */

export const DashboardScreen: React.FC<DashboardScreenProps> = ({
  claims, members, points, period, onPeriodChange, flow, flowRefreshing = false, flowError,
  updatedAt, refreshing = false, onRefresh, linkComponent,
}) => {
  const Link = resolveLink(linkComponent);
  const rangeText = period.from === period.to
    ? formatThaiDate(period.from)
    : `${formatThaiDate(period.from)} – ${formatThaiDate(period.to)}`;
  const flowTitle = `ในช่วง ${rangeText} (${period.interval === 'day' ? 'รายวัน' : 'รายเดือน'})`;

  /** ชั้น "ในช่วง" ของแต่ละการ์ด — สถานะเดียวกันทุกการ์ดเพราะมาจากคำขอเดียว */
  const renderFlow = (label: string, body: (f: PeriodFlow) => React.ReactNode) => {
    if (flow.state === 'unavailable') {
      return (
        <Layer title={flowTitle}>
          <p className="flex items-center gap-[var(--space-1)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
            <Icon name="info" size={16} />
            ระบบยังไม่มีข้อมูลตามช่วงเวลา
          </p>
        </Layer>
      );
    }
    if (flow.state === 'loading') return <Layer title={flowTitle}><Skeleton label={label} /></Layer>;
    if (flow.state === 'error') return <Layer title={flowTitle}><ErrorLine label={label} detail={flowError} /></Layer>;
    return <Layer title={flowTitle} dim={flowRefreshing}>{body(flow.data)}</Layer>;
  };

  /** ส่วนที่กว้างเต็มการ์ด (ใต้สองชั้น) — แสดงเฉพาะเมื่อมีข้อมูลในช่วง สถานะอื่นบอกไว้ในชั้นแล้ว */
  const renderFlowWide = (body: (f: PeriodFlow) => React.ReactNode) => {
    if (flow.state !== 'ready') return null;
    return (
      <div
        style={{ flex: '1 1 100%', minWidth: 0 }}
        className={cn('transition-opacity', flowRefreshing && 'opacity-50')}
        aria-busy={flowRefreshing || undefined}
      >
        {body(flow.data)}
      </div>
    );
  };

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="ภาพรวม"
        end={
          <div className="flex items-center gap-[var(--space-3)]">
            <span className="text-[length:var(--text-form)] text-[var(--ink-600)]" aria-live="polite">{updatedText(updatedAt)}</span>
            {onRefresh && (
              <button type="button" onClick={onRefresh} disabled={refreshing} className={secondaryBtnCls}>
                <Icon name="refresh" size={16} />
                {refreshing ? 'กำลังโหลด…' : 'โหลดใหม่'}
              </button>
            )}
          </div>
        }
      />

      {/* ตัวเลือกช่วง — แถวเดียวเหนือทุกการ์ด และมีผลกับทุกตัวเลข "ในช่วง" */}
      <div
        role="group"
        aria-label="ช่วงเวลาของข้อมูล"
        className="flex flex-wrap items-center gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-4)] py-[var(--space-2)]"
      >
        <span className="text-[length:var(--text-form)] text-[var(--ink-700)]">ช่วงเวลา</span>
        {PRESETS.map((p) => {
          const on = period.preset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              aria-pressed={on}
              onClick={() => onPeriodChange?.(p.id)}
              className={cn(
                'inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] border px-[var(--space-3)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2',
                on
                  ? 'border-[var(--blue-700)] bg-[var(--blue-050)] font-[var(--weight-semibold)] text-[var(--blue-700)]'
                  : 'border-[var(--line-300)] bg-[var(--surface-0)] text-[var(--ink-900)] hover:bg-[var(--blue-050)]',
              )}
            >
              {on && <Icon name="check_circle" size={16} />}
              {p.label}
            </button>
          );
        })}
        <DateRangePicker
          label="กำหนดช่วงเอง"
          presets={[]}
          value={{ from: period.from, to: period.to }}
          onChange={(r) => { if (r.from && r.to) onPeriodChange?.('custom', { from: r.from, to: r.to }); }}
        />
        <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
          ไม่เกิน 92 วันแสดงรายวัน · ยาวกว่านั้นแสดงรายเดือน (สูงสุด 24 เดือน)
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-[var(--space-4)]">
        {/* การ์ดละหนึ่งแถว เรียงตามลำดับงาน: คำขอ → บิล → คะแนน → สมาชิก */}
        <div className="flex flex-col gap-[var(--space-4)]">
          {/* ── คำขอสะสมคะแนน ── */}
          <Card
            title="คำขอสะสมคะแนน"
            icon="inbox"
            action={
              <Link to="/queue/claims" className={secondaryBtnCls} style={{ textDecoration: 'none' }}>
                ไปที่คิวตรวจ
                <Icon name="arrow_forward" size={16} />
              </Link>
            }
          >
            <Layer title="ณ ตอนนี้" size="now">
              <NowBody section={claims} label="ข้อมูลคำขอ">
                {(c) => (
                  <StatGrid>
                    <Stat
                      label="รอตรวจ" icon="pending" value={num(c.pending)} unit="รายการ" emphasis
                      note={c.oldestPendingHours !== null ? `ค้างนานที่สุด ${ageText(c.oldestPendingHours)}` : 'ไม่มีคำขอค้าง'}
                    />
                    <Stat
                      label={`ค้างเกิน ${num(c.slaHours)} ชั่วโมง`}
                      icon={c.overdue > 0 ? 'warning' : 'timer'}
                      value={`${num(c.overdue)}${c.overdueIsLowerBound ? '+' : ''}`}
                      unit="รายการ"
                      tone={c.overdue > 0 ? 'warning' : 'default'}
                      note={c.overdue > 0
                        ? (c.overdueIsLowerBound ? 'เกินเวลาที่กำหนด — นับจากรายการชุดแรก ตัวจริงอาจมากกว่านี้' : 'เกินเวลาที่กำหนด ควรตรวจก่อน')
                        : 'ไม่มีคำขอเกินเวลา'}
                    />
                    {c.pendingCanceledReceipt !== undefined && c.pendingCanceledReceipt > 0 && (
                      <Stat
                        label="ใบเสร็จถูกยกเลิกแล้ว"
                        icon="sync_problem"
                        value={num(c.pendingCanceledReceipt)}
                        unit="รายการ"
                        tone="warning"
                        wide
                        note="คำขอที่ค้างอยู่แต่ใบเสร็จในระบบถูกยกเลิกไปแล้ว — ไม่ได้นับใน &quot;รอตรวจ&quot; ควรปฏิเสธ"
                      />
                    )}
                  </StatGrid>
                )}
              </NowBody>
            </Layer>

            {renderFlow('ข้อมูลคำขอตามช่วง', (f) => (
              <>
                <StatGrid>
                  <Stat label="ส่งเข้ามา" value={num(f.claims.submitted)} unit="รายการ" />
                  <Stat label="อนุมัติ" icon="check_circle" value={num(f.claims.approved)} unit="รายการ" />
                  <Stat label="ปฏิเสธ" icon="cancel" value={num(f.claims.rejected)} unit="รายการ" />
                  <Stat label="ยอดเงินที่อนุมัติ" value={baht(f.claims.approvedAmount)} unit="บาท" />
                  <Stat
                    label="เวลาตรวจเฉลี่ย"
                    icon="schedule"
                    value={f.claims.avgReviewHours === null ? '—' : num(f.claims.avgReviewHours, 1)}
                    unit={f.claims.avgReviewHours === null ? undefined : 'ชั่วโมง'}
                    note="ไม่นับคำขอจากใบเสร็จในระบบที่อนุมัติอัตโนมัติ"
                  />
                </StatGrid>
                {f.claims.channels.length > 0 && (
                  <table className="w-full text-[length:var(--text-table-sm)] leading-[var(--leading-table)]">
                    <caption className="sr-only">คำขอแยกตามช่องทาง</caption>
                    <thead>
                      <tr className="text-left text-[var(--ink-600)]">
                        <th scope="col" className="py-[var(--space-1)] pr-[var(--space-2)] font-[var(--weight-medium)]">ช่องทาง</th>
                        <th scope="col" className="py-[var(--space-1)] pr-[var(--space-2)] text-right font-[var(--weight-medium)]">ส่ง</th>
                        <th scope="col" className="py-[var(--space-1)] pr-[var(--space-2)] text-right font-[var(--weight-medium)]">อนุมัติ</th>
                        <th scope="col" className="py-[var(--space-1)] pr-[var(--space-2)] text-right font-[var(--weight-medium)]">ปฏิเสธ</th>
                        <th scope="col" className="py-[var(--space-1)] text-right font-[var(--weight-medium)]">ยอดอนุมัติ (บาท)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {f.claims.channels.map((ch) => (
                        <tr key={ch.label} className="border-t border-[var(--line-200)]">
                          <th scope="row" className="py-[var(--space-1)] pr-[var(--space-2)] text-left font-[var(--weight-medium)] text-[var(--ink-900)]">{ch.label}</th>
                          <td className="bo-num py-[var(--space-1)] pr-[var(--space-2)] text-[var(--ink-900)]">{num(ch.submitted)}</td>
                          <td className="bo-num py-[var(--space-1)] pr-[var(--space-2)] text-[var(--ink-900)]">{num(ch.approved)}</td>
                          <td className="bo-num py-[var(--space-1)] pr-[var(--space-2)] text-[var(--ink-900)]">{num(ch.rejected)}</td>
                          <td className="bo-num py-[var(--space-1)] text-[var(--ink-900)]">{baht(ch.approvedAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ))}

            {/* กราฟกว้างเต็มการ์ด — ช่วง 30 วันมี 30 แท่ง ต้องการความกว้างมากกว่าคอลัมน์ขวา */}
            {renderFlowWide((f) => (
              <>
                {/* กราฟชุดเดียวไม่มีคำอธิบายสี — ชื่อกราฟต้องบอกเองว่าแท่งคืออะไร */}
                <h4 className="mb-[var(--space-1)] text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] leading-[var(--leading-table)] text-[var(--ink-600)]">
                  คำขอที่ส่งเข้ามา ({period.interval === 'day' ? 'รายวัน' : 'รายเดือน'})
                </h4>
                <TrendChart
                title="คำขอที่ส่งเข้ามา"
                integer
                kind="column"
                labels={f.labels}
                fullLabels={f.fullLabels}
                series={[{ key: 'submitted', label: 'ส่งเข้ามา', color: 'var(--chart-series-1)', values: f.claims.submittedSeries }]}
                format={(n) => num(n)}
                unit="รายการ"
              />
              </>
            ))}
          </Card>

          {/* ── บิลที่ขายในระบบ ── */}
          <Card title="บิลที่ขายในระบบ" icon="receipt_long">
            {renderFlow('ข้อมูลบิล', (f) => {
              const o = f.orders;
              const memberShare = o.net > 0 ? (o.memberNet / o.net) * 100 : 0;
              return (
                <>
                  <StatGrid>
                    <Stat label="ยอดขายสุทธิ" value={baht(o.net)} unit="บาท" tone="brand" emphasis wide
                      note="ยอดที่ลูกค้าชำระ ไม่รวมบิลที่ถูกยกเลิก · ยังไม่หักยอดคืนเงิน" />
                    <Stat label="จำนวนบิล" value={num(o.count)} unit="บิล" />
                    <Stat label="เฉลี่ยต่อบิล" value={baht(o.avgTicket)} unit="บาท" />
                    <Stat label="ก่อนส่วนลดท้ายบิล" value={baht(o.gross)} unit="บาท" />
                    <Stat label="ส่วนลดท้ายบิล" value={baht(o.discount)} unit="บาท" note="รวมส่วนลดจากคูปอง" />
                    <Stat label="คืนเงิน (ใบลดหนี้)" value={baht(o.refunded)} unit="บาท" />
                    <Stat label="บิลที่ถูกยกเลิกภายหลัง" value={num(o.canceledCount)} unit="บิล" />
                    <Stat
                      label="ยอดจากสมาชิก"
                      icon="groups"
                      value={baht(o.memberNet)}
                      unit="บาท"
                      note={`${num(o.memberCount)} บิล · ${num(memberShare, 1)}% ของยอดขายสุทธิ`}
                      wide
                    />
                  </StatGrid>
                  <TrendChart
                    title="ยอดขายสุทธิ"
                    kind="column"
                    labels={f.labels}
                    fullLabels={f.fullLabels}
                    series={[{ key: 'net', label: 'ยอดขายสุทธิ', color: 'var(--chart-series-1)', values: o.netSeries }]}
                    format={(n) => baht(n)}
                    unit="บาท"
                  />
                </>
              );
            })}
          </Card>

          {/* ── คะแนน ── */}
          <Card title="คะแนน" icon="savings">
            <Layer title="ณ ตอนนี้" size="now">
              <NowBody section={points} label="ข้อมูลคะแนน">
                {(p) => (
                  <StatGrid>
                    <Stat
                      label="คะแนนคงค้าง (Point Liability)" icon="savings" value={pts(p.outstanding)} unit="คะแนน"
                      tone="brand" emphasis wide
                      note="คะแนนแลกของที่ยังไม่หมดอายุ ลูกค้านำไปแลกได้ ณ ตอนนี้ · มูลค่าเป็นบาทรอฝ่ายบัญชีกำหนด"
                    />
                    <Stat label="สมาชิกที่มีคะแนน" icon="groups" value={num(p.accounts)} unit="คน" />
                    <Stat
                      label={`หมดอายุใน ${num(p.expireDays)} วัน`} icon="event_busy" value={pts(p.expiring)} unit="คะแนน"
                      tone={p.expiring > 0 ? 'warning' : 'default'}
                      note={p.outstanding > 0 ? `${num((p.expiring / p.outstanding) * 100, 1)}% ของคะแนนคงค้าง` : undefined}
                    />
                  </StatGrid>
                )}
              </NowBody>
            </Layer>

            {renderFlow('ข้อมูลคะแนนตามช่วง', (f) => (
              <>
                <StatGrid>
                  <Stat label="ให้จากคำขอ" icon="trending_up" value={pts(f.points.earned)} unit="คะแนน" note="หักคำขอที่ถูกย้อนในช่วงแล้ว" />
                  <Stat label="ใช้แลกของรางวัล" icon="redeem" value={pts(f.points.redeemed)} unit="คะแนน" />
                  <Stat label="หมดอายุ" icon="event_busy" value={pts(f.points.expired)} unit="คะแนน" />
                  <Stat
                    label="ปรับโดยเจ้าหน้าที่"
                    icon="edit_note"
                    value={`+${pts(f.points.adjustedAdd)} / −${pts(f.points.adjustedDeduct)}`}
                    unit="คะแนน"
                    note="เพิ่ม / หัก หลังหักรายการที่ยกเลิก"
                  />
                </StatGrid>
                <TrendChart
                  title="คะแนนที่ให้และที่ใช้แลก"
                  kind="line"
                  labels={f.labels}
                  fullLabels={f.fullLabels}
                  series={[
                    { key: 'earned', label: 'ให้จากคำขอ', color: 'var(--chart-series-1)', values: f.points.earnedSeries },
                    { key: 'redeemed', label: 'ใช้แลกของรางวัล', color: 'var(--chart-series-2)', values: f.points.redeemedSeries },
                  ]}
                  format={(n) => pts(n)}
                  unit="คะแนน"
                />
              </>
            ))}
          </Card>

          {/* ── สมาชิก ── */}
          <Card title="สมาชิก" icon="groups">
            <Layer title="ณ ตอนนี้" size="now">
              <NowBody section={members} label="ข้อมูลสมาชิก">
                {(m) => {
                  const inTier = m.byTier.reduce((s, t) => s + t.count, 0);
                  const noTier = m.withoutTier ?? Math.max(0, m.total - inTier);
                  const rows = noTier > 0 ? [...m.byTier, { name: 'ยังไม่มีระดับ', count: noTier }] : m.byTier;
                  return (
                    <>
                      <StatGrid>
                        <Stat label="สมาชิกทั้งหมด" icon="groups" value={num(m.total)} unit="คน" emphasis />
                      </StatGrid>
                      {rows.length > 0 && (
                        <table className="w-full text-[length:var(--text-form)] leading-[var(--leading-table)]">
                          <caption className="sr-only">จำนวนสมาชิกแยกตามระดับ</caption>
                          <thead>
                            <tr className="text-left text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
                              <th scope="col" className="py-[var(--space-1)] pr-[var(--space-2)] font-[var(--weight-medium)]">ระดับ</th>
                              <th scope="col" className="py-[var(--space-1)] pr-[var(--space-2)] text-right font-[var(--weight-medium)]">สมาชิก (คน)</th>
                              <th scope="col" className="py-[var(--space-1)] text-right font-[var(--weight-medium)]">สัดส่วน</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((t) => {
                              const pct = m.total > 0 ? (t.count / m.total) * 100 : 0;
                              return (
                                <tr key={t.name} className="border-t border-[var(--line-200)]">
                                  <th scope="row" className="py-[var(--space-1)] pr-[var(--space-2)] text-left font-[var(--weight-medium)] text-[var(--ink-900)]">
                                    {t.name}
                                    {/* แถบสัดส่วน — ตัวเลขอยู่คอลัมน์ขวาแล้ว แถบเป็นตัวช่วยกวาดตา */}
                                    <span aria-hidden="true" className="mt-[var(--space-05)] block h-[var(--space-15)] rounded-[var(--radius-sm)] bg-[var(--surface-100)]">
                                      <span className="block h-full rounded-[var(--radius-sm)] bg-[var(--chart-series-1)]" style={{ width: `${pct}%` }} />
                                    </span>
                                  </th>
                                  <td className="bo-num py-[var(--space-1)] pr-[var(--space-2)] text-[var(--ink-900)]">{num(t.count)}</td>
                                  <td className="bo-num py-[var(--space-1)] text-[var(--ink-700)]">{num(pct, 1)}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </>
                  );
                }}
              </NowBody>
            </Layer>

            {renderFlow('ข้อมูลสมาชิกตามช่วง', (f) => (
              <>
                <StatGrid>
                  <Stat
                    label="สมาชิกใหม่" icon="trending_up" value={num(f.members.newInPeriod)} unit="คน"
                    note={f.members.unknownJoinDate > 0
                      ? `ไม่รวมบัญชีเก่า ${num(f.members.unknownJoinDate)} บัญชีที่ไม่ทราบวันสมัคร`
                      : undefined}
                  />
                </StatGrid>
                <TrendChart
                  title="สมาชิกใหม่"
                  integer
                  kind="column"
                  labels={f.labels}
                  fullLabels={f.fullLabels}
                  series={[{ key: 'new', label: 'สมาชิกใหม่', color: 'var(--chart-series-1)', values: f.members.newSeries }]}
                  format={(n) => num(n)}
                  unit="คน"
                />
              </>
            ))}
          </Card>
        </div>

        {flow.state === 'unavailable' && (
          <Callout tone="info" title="ยังไม่มีข้อมูลตามช่วงเวลา" className="mt-[var(--space-4)]">
            ระบบที่ต่ออยู่ยังไม่รองรับข้อมูลสรุปตามช่วงเวลา จึงแสดงได้เฉพาะตัวเลข ณ ตอนนี้
          </Callout>
        )}
      </div>
    </div>
  );
};

export default DashboardScreen;
